import * as vp from 'vopay';
import * as Yup from 'yup';
import { FunctionComponent, JSX, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { buildAccountTransferRows } from './buildAccountTransferRows.helper';
import { AccountTransferHandler, AccountTransferRequest } from './types';
import { useClientData } from '../../../providers/ClientData.provider';
import usePostAccountBalance from '../../../hooks/useApi/usePostAccountBalance';

const { Box, Button, Table, TextField, Select, Icon } = vp;

type AccountTransferProps = {
  defaultValues: AccountTransferRequest;
  handleToken: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlers: AccountTransferHandler;
  subAccounts: Record<string, vp.SubAccountsLists>;
};

type FC = FunctionComponent<AccountTransferProps>;
const AccountTransferForm: FC = ({ defaultValues, handleToken, handlers, subAccounts }) => {
  const { response } = useClientData();
  const [accountBalanceResponse, getAccountBalance] = usePostAccountBalance({});
  const balances = Object.values(
    Object.values(subAccounts).find((subAccount) => subAccount.AccountID === defaultValues.From)?.Balances || [],
  );
  const defaultAvailableBalance =
    balances.filter((balancesSub) => balancesSub.Currency === defaultValues.Currency)[0]?.AvailableBalance || '0.00';

  const [maximumAmount, setMaximumAmount] = useState(Number(defaultAvailableBalance));
  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onAccountTransfer}
      validationSchema={createdValidationSchema(maximumAmount)}
      validateOnChange
      validateOnBlur
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit }) => {
        const AccountTransferRow = buildAccountTransferRows({
          AccountID: values.From,
          Currency: values.Currency,
          MainAccountId: response.AccountID,
          MainAccountName: response.AccountName,
          MainAccountBalance: accountBalanceResponse?.AvailableFunds || '0.00',
          subAccounts,
          country: response.Country,
        });
        useEffect(() => {
          if (values.From === response.AccountID) {
            setMaximumAmount(Number(accountBalanceResponse?.AvailableFunds || '0.00'));
          } else {
            const changebalances = Object.values(
              Object.values(subAccounts).find((subAccount) => subAccount.AccountID === values.From)?.Balances || [],
            );
            const defaultAvailableBalanceChange =
              changebalances.filter((balancesSub) => balancesSub.Currency === values.Currency)[0]?.AvailableBalance ||
              '0.00';
            setMaximumAmount(Number(defaultAvailableBalanceChange));
          }
        }, [values.From, values.Currency, accountBalanceResponse]);
        useEffect(() => {
          if (response.Success) {
            getAccountBalance({
              PortalUser: response.LoginUserName,
              Currency: values.Currency,
            });
          }
        }, [values.Currency]);
        const fundTable: vp.TableProps = {
          name: 'sub-account-transfer-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: AccountTransferRow.map(({ label, id, require, preset, type, placeholder, getSelectOptions }) => ({
            Field: (
              <Box ai="center">
                {label} {getRequiredIcon(require)}
              </Box>
            ),
            Input:
              type === 'input' ? (
                <TextField
                  preset={preset}
                  key={id}
                  name={id}
                  variant="standard"
                  value={String(values[id])}
                  placeholder={placeholder}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              ) : (
                <Select
                  variant="standard"
                  options={getSelectOptions()}
                  key={id}
                  name={id}
                  value={String(values[id])}
                  placeholder={placeholder}
                  noOrdering
                  onBlur={handleBlur}
                  onChange={(e) => {
                    handleChange(e);
                    if (id === 'From') {
                      handleToken(e);
                    } else {
                      handleChange(e);
                    }
                  }}
                  error={errors[id] && touched[id] ? errors[id] : null}
                />
              ),
          })),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Table {...fundTable} />
            <Box mt="md" jc="fe">
              <Button id="Account-Transfer-Review" type="submit" label="Review" />
            </Box>
          </form>
        );
      }}
    </Formik>
  );

  function createdValidationSchema(availableBalance: number) {
    return Yup.object().shape({
      Currency: Yup.string().required('This value is required.'),
      From: Yup.string().required('This value is required.'),
      To: Yup.string().required('This value is required.'),
      Amount: Yup.string()
        .test('amount-validation', 'This value should be greater than or equal to $0.01.', (value) => {
          const amountValidation = Number(value) <= 0.0;
          return !amountValidation;
        })
        .test('amount-validation', `Insufficient funds. Maximum allowed is: ${String(availableBalance)}`, (value) => {
          const availableFundsValidation = Number(value) > Number(availableBalance);
          return !availableFundsValidation;
        })
        .required('This value is required.'),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
};

export default AccountTransferForm;
